import React, { memo, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import useErrorNotification from 'src/hooks/useErrorNotification';

import Button from 'src/components/Button';

import theme from 'src/styles/components/SubmitOrderButton.module.scss';
import { BILL_STATUS } from 'src/modules/Order';

const ExternalPaymentButton = memo(({
    context,
    submitOrder,
    isSubmitOrderPending,
    canOrder,
    error,
    payment,
    lastBillStatus,
    lastBillSummary,
    repayment,
    cancelOrder,
    cancelOrderError,
    clearSubmitErrorMessage,
    clearCancelOrderErrorMessage,
    repaymentError,
    clearRepaymentErrorMessage
}) => {
    const intl = useIntl();

    useErrorNotification({ message: error, cancelFunc: clearSubmitErrorMessage });
    useErrorNotification({ message: repaymentError, cancelFunc: clearRepaymentErrorMessage });
    useErrorNotification({ message: cancelOrderError, cancelFunc: clearCancelOrderErrorMessage });

    useEffect(() => {
        if (payment?.paymentUrl) {
            window.location.replace(payment?.paymentUrl);
        }
    }, [payment?.paymentUrl]);

    const handleOrder = useCallback(() => {
        if (!isSubmitOrderPending) {
            submitOrder(context, window.location.href);
        }
    }, [context, isSubmitOrderPending]);

    const handleRepayment = useCallback(() => {
        if (!isSubmitOrderPending) {
            repayment(context, window.location.href);
        }
    }, [context, isSubmitOrderPending]);

    const handleCancelOrder = useCallback(() => {
        if (!isSubmitOrderPending) {
            cancelOrder(context);
        }
    }, [context, isSubmitOrderPending]);

    if (!lastBillStatus || lastBillStatus === BILL_STATUS.ABORTED || lastBillStatus === BILL_STATUS.PAID) {
        return (
            <Button
              className={theme.button}
              onClick={handleOrder}
              disabled={!canOrder}
              raised
              accent
            >
                {intl.formatMessage({ id: 'App.makeOrderAndPay' })}
                {Boolean(lastBillSummary) && (
                    <>
                        {' '}-{' '}
                        {intl.formatNumber(lastBillSummary, { style: 'currency', currency: 'PLN' })}
                    </>
                )}
            </Button>
        );
    }

    if (lastBillStatus === BILL_STATUS.CLOSED) {
        // Button with payment link without order
        return (
            <>
                <Button
                  className={classnames(theme.button)}
                  onClick={handleCancelOrder}
                  secondary
                >
                    {intl.formatMessage({ id: 'App.cancel' })}
                </Button>
            </>
        );
    }

    if (lastBillStatus === BILL_STATUS.PAYMENT_ERROR) {
        return (
            <>
                <Button
                  className={classnames(theme.button, theme.left)}
                  onClick={handleCancelOrder}
                  secondary
                >{intl.formatMessage({ id: 'App.cancel' })}</Button>
                <Button
                  className={classnames(theme.button, theme.right)}
                  onClick={handleRepayment}
                  raised
                  accent
                >{intl.formatMessage({ id: 'App.retryPayment' })}</Button>
            </>
        );
    }

    return null;
});

ExternalPaymentButton.propTypes = {
    context: PropTypes.object,
    submitOrder: PropTypes.func.isRequired,
    isSubmitOrderPending: PropTypes.bool.isRequired,
    canOrder: PropTypes.bool.isRequired,
    error: PropTypes.string,
    payment: PropTypes.shape({
        paymentUrl: PropTypes.string.isRequired
    }),
    lastBillStatus: PropTypes.number.isRequired,
    lastBillSummary: PropTypes.number,
    repayment: PropTypes.func.isRequired,
    cancelOrder: PropTypes.func.isRequired,
    clearSubmitErrorMessage: PropTypes.func.isRequired,
    cancelOrderError: PropTypes.string,
    clearCancelOrderErrorMessage: PropTypes.func.isRequired,
    repaymentError: PropTypes.string,
    clearRepaymentErrorMessage: PropTypes.func.isRequired
};

export default ExternalPaymentButton;
